import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';

/**
 * ## "Nothing here" and "I could not read it" must never look alike
 *
 * This is a bug this system has already shipped: an empty result rendered as
 * "nothing configured", when in fact the query was broken and the venue's
 * printers were sitting there perfectly configured behind it. Somebody spent an
 * evening re-adding hardware that already existed.
 *
 * The backend has the same hazard from the other side and names it in
 * `print-errors.ts`: the runtime database role is `NOBYPASSRLS`, so a read with
 * no tenant bound returns **zero rows rather than an error** — which is exactly
 * how a broken query arrives dressed as an empty one. That is why the printing
 * services raise loud, coded refusals instead of returning `[]`, and why this
 * component refuses to draw the two the same way.
 *
 * Three outcomes, three shapes:
 *
 *  - **Failed** — a bordered, tinted `role="alert"` panel carrying the server's
 *    own words and a Retry. It is a *box*, not centred grey text, so it is not
 *    mistakable for a list that happens to be short.
 *  - **Empty, unfiltered** — the venue has genuinely configured nothing. An
 *    inbox glyph and, where there is one, the button that fixes it.
 *  - **Empty, filtered** — rows exist, these filters exclude them. A different
 *    glyph and a "clear filters" button, so nobody concludes the venue is
 *    unconfigured because a status dropdown was left on `DEAD`.
 */
export interface PanelResultProps {
  loading: boolean;
  /** The message the API actually sent, or null when the load succeeded. */
  error: string | null;
  /** How many rows came back. */
  count: number;
  onRetry: () => void;
  /** Names the thing that failed to load: "the print spool", "printers". */
  subject: string;
  /** True when any filter is narrowing the request. */
  filtered?: boolean;
  onClearFilters?: () => void;
  /** Shown when nothing is configured at all. */
  emptyTitle: string;
  emptyHint?: string;
  /** The button that resolves the empty state — usually "Add …". */
  emptyAction?: ReactNode;
  children: ReactNode;
}

export function PanelResult({
  loading,
  error,
  count,
  onRetry,
  subject,
  filtered,
  onClearFilters,
  emptyTitle,
  emptyHint,
  emptyAction,
  children,
}: PanelResultProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50/70 p-6 text-center dark:border-red-900 dark:bg-red-950/30"
      >
        <div className="grid place-items-center gap-2">
          <Icon as={TriangleAlert} size="xl" className="text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium">{t('fleet.states.loadFailed', { subject })}</p>
          {/*
              The server's own message, verbatim. A generic "something went
              wrong" is what makes a broken query indistinguishable from an
              empty one all over again — this is the sentence a support engineer
              needs, and the reason the API sends coded refusals at all.
          */}
          <p className="max-w-lg text-sm text-ink-muted">{error}</p>
          <p className="max-w-lg text-xs text-ink-muted">{t('fleet.states.loadFailedHint')}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
            <Icon as={RefreshCw} />
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  if (count === 0 && filtered) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={SearchX} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{t('fleet.states.noMatches')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('fleet.states.noMatchesHint')}</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t('fleet.states.clearFilters')}
          </Button>
        )}
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={Inbox} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyHint && <p className="max-w-sm text-sm text-ink-muted">{emptyHint}</p>}
        {emptyAction}
      </div>
    );
  }

  return <>{children}</>;
}

export default PanelResult;
